import type { ShiftAggregate, ZonedMoment } from '@der-maschinfuehrer/contracts';
import { useState, type FormEvent } from 'react';

import { FormErrorSummary, TextField } from '../../components/form-primitives';
import { OptionalZonedMomentField } from '../../components/OptionalZonedMomentField';
import { shiftAggregateToMutation } from '../../domain/alpha-shift';
import { AppShiftConflictError, type AppStorage } from '../../storage/app-storage';

export function ShiftCloseForm({
  aggregate,
  onSaved,
  storage,
}: {
  readonly aggregate: ShiftAggregate;
  readonly onSaved: (aggregate: ShiftAggregate, message: string) => void;
  readonly storage: AppStorage;
}) {
  const [expanded, setExpanded] = useState(false);
  const [actualStart, setActualStart] = useState<ZonedMoment | null>(aggregate.shift.actual_start);
  const [actualEnd, setActualEnd] = useState<ZonedMoment | null>(aggregate.shift.actual_end);
  const [homeBase, setHomeBase] = useState(aggregate.shift.home_base ?? '');
  const [shiftType, setShiftType] = useState(aggregate.shift.shift_type ?? '');
  const [note, setNote] = useState(aggregate.shift.note);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const timeWarning =
    actualStart !== null &&
    actualEnd !== null &&
    Date.parse(actualEnd.instant) < Date.parse(actualStart.instant)
      ? 'Skutečný konec směny je dříve než začátek. Hodnoty lze uložit, ale zkontroluj datum a UTC offset.'
      : null;

  const save = async (status: 'open' | 'closed') => {
    setErrors([]);
    setSaving(true);
    try {
      const mutation = shiftAggregateToMutation(aggregate);
      const saved = await storage.saveShift(
        {
          ...mutation,
          shift: {
            ...mutation.shift,
            actual_start: actualStart,
            actual_end: actualEnd,
            home_base: nullableTrimmed(homeBase),
            shift_type: nullableTrimmed(shiftType),
            note,
            status,
          },
        },
        aggregate.aggregate_revision,
      );
      onSaved(
        saved,
        status === 'closed'
          ? 'Směna je uzavřená. Neuvedené skutečné časy zůstaly neznámé.'
          : 'Směna je znovu výslovně otevřená.',
      );
      setExpanded(false);
      setSaving(false);
    } catch (error) {
      setErrors([
        error instanceof AppShiftConflictError
          ? 'Směna byla mezitím změněna v jiném okně. Načti její aktuální verzi.'
          : error instanceof Error
            ? `Směnu se nepodařilo uložit. ${error.message}`
            : 'Směnu se nepodařilo uložit.',
      ]);
      setSaving(false);
    }
  };

  if (aggregate.shift.status === 'closed' && !expanded) {
    return (
      <section className="shift-close-panel" aria-label="Stav směny">
        <h3>Směna je uzavřená</h3>
        <p>Skutečné časy lze doplnit jen výslovnou editací.</p>
        <div className="form-actions">
          <button
            type="button"
            className="secondary-action touch-target"
            onClick={() => setExpanded(true)}
          >
            Upravit údaje uzavřené směny
          </button>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={saving}
            onClick={() => void save('open')}
          >
            {saving ? 'Otevírám směnu…' : 'Znovu otevřít směnu'}
          </button>
        </div>
        <FormErrorSummary errors={errors} />
      </section>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        className="secondary-action touch-target"
        onClick={() => setExpanded(true)}
      >
        Uzavřít směnu a doplnit údaje
      </button>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void save('closed');
  };

  return (
    <section className="shift-close-panel" aria-label="Údaje a uzavření směny">
      <h3>{aggregate.shift.status === 'closed' ? 'Upravit směnu' : 'Uzavřít směnu'}</h3>
      <p>Začátek ani konec se nedoplní okamžikem uložení. Neznámé časy nech jednoduše neuvedené.</p>
      <FormErrorSummary errors={errors} />
      {timeWarning ? (
        <p className="draft-conflict" role="status">
          {timeWarning}
        </p>
      ) : null}
      <form aria-label="Údaje směny" onSubmit={handleSubmit}>
        <OptionalZonedMomentField
          label="Skutečný začátek směny"
          name="shift-actual-start"
          value={actualStart}
          disabled={saving}
          onChange={setActualStart}
        />
        <OptionalZonedMomentField
          label="Skutečný konec směny"
          name="shift-actual-end"
          value={actualEnd}
          disabled={saving}
          onChange={setActualEnd}
        />
        <TextField
          label="Domovské pracoviště nebo depo"
          name="shift-home-base"
          value={homeBase}
          maxLength={256}
          disabled={saving}
          onChange={(event) => setHomeBase(event.currentTarget.value)}
        />
        <TextField
          label="Druh směny"
          name="shift-type"
          value={shiftType}
          maxLength={128}
          disabled={saving}
          onChange={(event) => setShiftType(event.currentTarget.value)}
        />
        <label className="form-field">
          <span>Poznámka ke směně</span>
          <textarea
            className="text-input"
            name="shift-note"
            value={note}
            maxLength={10_000}
            disabled={saving}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
        </label>
        <div className="form-actions">
          <button type="submit" className="primary-action touch-target" disabled={saving}>
            {saving
              ? 'Ukládám směnu…'
              : aggregate.shift.status === 'closed'
                ? 'Uložit údaje uzavřené směny'
                : 'Uzavřít směnu'}
          </button>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={saving}
            onClick={() => setExpanded(false)}
          >
            Zrušit
          </button>
        </div>
      </form>
    </section>
  );
}

function nullableTrimmed(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
